import type { ApiClient } from '../api/client';
import type { Conversation, ConversationGitlabInfo } from '../types';

import { createLiveResource, type LiveResource } from './liveResource';

const MAX_GITLAB_BATCH = 50;

export type ConversationListQuery = {
  includeArchived: boolean;
  searchQuery: string;
};

export type ConversationListData = {
  conversations: Conversation[];
};

export type ConversationListResource = {
  key: string;
  live: LiveResource<ConversationListData>;
  forceRefreshGitlab: () => Promise<void>;
  reauthorize: () => Promise<void>;
};

const resourcesByClient = new WeakMap<ApiClient, Map<string, ConversationListResource>>();

function resolveConversationListSource(reason: string): string {
  if (reason === 'poll') {
    return 'poller';
  }
  if (reason === 'subscribe' || reason === 'bootstrap' || reason === 'manual') {
    return 'direct';
  }
  return reason;
}

function normalizeQuery(query: ConversationListQuery): ConversationListQuery {
  return {
    includeArchived: query.includeArchived,
    searchQuery: query.searchQuery.trim()
  };
}

function buildQueryKey(query: ConversationListQuery): string {
  return `${query.includeArchived ? 'archived' : 'active'}:${query.searchQuery}`;
}

function buildConversationsPath(query: ConversationListQuery): string {
  const params = new URLSearchParams();
  params.set('limit', '200');
  if (query.includeArchived) {
    params.set('include_archived', 'true');
  }
  if (query.searchQuery) {
    params.set('search', query.searchQuery);
  }
  return `/api/conversations?${params.toString()}`;
}

function applyGitlabInfo(
  conversations: Conversation[],
  gitlabMap: Map<string, ConversationGitlabInfo | null>,
  previousConversations: Conversation[] = []
): Conversation[] {
  if (gitlabMap.size === 0 && previousConversations.length === 0) {
    return conversations;
  }
  const previousById = new Map(
    previousConversations.map((conversation) => [conversation.id, conversation])
  );
  return conversations.map((conversation) => {
    const cached = gitlabMap.get(conversation.id);
    if (cached !== undefined) {
      if (cached !== null) {
        return {
          ...conversation,
          gitlab: cached
        };
      }
      const previous = previousById.get(conversation.id);
      if (previous?.gitlab !== undefined) {
        return {
          ...conversation,
          gitlab: previous.gitlab
        };
      }
      return conversation;
    }
    const previous = previousById.get(conversation.id);
    if (previous?.gitlab !== undefined) {
      return {
        ...conversation,
        gitlab: previous.gitlab
      };
    }
    return conversation;
  });
}

async function loadGitlabBatch(
  apiClient: ApiClient,
  ids: string[],
  forceRefresh = false
): Promise<Map<string, ConversationGitlabInfo | null>> {
  if (ids.length === 0) {
    return new Map();
  }
  const endpoint = forceRefresh
    ? '/api/conversations/gitlab?force_refresh=true'
    : '/api/conversations/gitlab';
  const data = await apiClient.post<{
    items: { conversation_id: string; gitlab: ConversationGitlabInfo | null }[];
  }>(endpoint, {
    conversation_ids: ids
  });
  return new Map(data.items.map((item) => [item.conversation_id, item.gitlab ?? null]));
}

function hasActiveGitlabPipeline(conversation: Conversation): boolean {
  const status = conversation.gitlab?.latest_pipeline?.status?.trim().toLowerCase();
  if (!status) {
    return false;
  }
  return !['success', 'failed', 'canceled', 'skipped'].includes(status);
}

function resolvePollInterval(data: ConversationListData | null): number | null {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return null;
  }
  if (!data) {
    return 4000;
  }
  const hasActivePipeline = data.conversations.some(hasActiveGitlabPipeline);
  if (hasActivePipeline) {
    return 5000;
  }
  if (data.conversations.length === 0) {
    return 10000;
  }
  return 15000;
}

function createConversationListResource(
  apiClient: ApiClient,
  normalizedQuery: ConversationListQuery,
  key: string
): ConversationListResource {
  let liveRef: LiveResource<ConversationListData> | null = null;
  const live = createLiveResource<ConversationListData>({
    id: `conversation-list:${key}`,
    fetch: async () => {
      const previous = liveRef?.getSnapshot().data;
      const conversations = await apiClient.get<Conversation[]>(
        buildConversationsPath(normalizedQuery)
      );
      const ids = conversations.slice(0, MAX_GITLAB_BATCH).map((conversation) => conversation.id);
      const gitlab = await loadGitlabBatch(apiClient, ids, false);
      return {
        conversations: applyGitlabInfo(conversations, gitlab, previous?.conversations ?? [])
      };
    },
    resolveSource: (_nextData, _previousData, reason) => resolveConversationListSource(reason),
    pollIntervalMs: (snapshot) => resolvePollInterval(snapshot.data),
    isEqual: (a, b) => JSON.stringify(a) === JSON.stringify(b)
  });
  liveRef = live;

  const forceRefreshGitlab = async () => {
    const current = live.getSnapshot().data;
    if (!current || current.conversations.length === 0) {
      await live.refresh('manual');
      return;
    }
    const ids = current.conversations
      .slice(0, MAX_GITLAB_BATCH)
      .map((conversation) => conversation.id);
    const gitlab = await loadGitlabBatch(apiClient, ids, true);
    live.updateLocal(
      (existing) => {
        if (!existing) {
          return existing;
        }
        return {
          conversations: applyGitlabInfo(
            existing.conversations,
            gitlab,
            existing.conversations
          )
        };
      },
      'gitlab-force-refresh'
    );
  };

  const reauthorize = async () => {
    // Returning to a backgrounded tab is an authorization boundary: project
    // membership may have changed while polling was intentionally paused.
    // Clear titles/status/GitLab metadata before the network round trip and
    // detach any request that began under the previous membership state.
    live.invalidate('foreground-reauthorize');
    await live.refresh('foreground-reauthorize');
  };

  return {
    key,
    live,
    forceRefreshGitlab,
    reauthorize
  };
}

export function getConversationListResource(
  apiClient: ApiClient,
  query: ConversationListQuery
): ConversationListResource {
  const normalizedQuery = normalizeQuery(query);
  const key = buildQueryKey(normalizedQuery);

  let byKey = resourcesByClient.get(apiClient);
  if (!byKey) {
    byKey = new Map<string, ConversationListResource>();
    resourcesByClient.set(apiClient, byKey);
  }
  const existing = byKey.get(key);
  if (existing) {
    return existing;
  }

  const created = createConversationListResource(apiClient, normalizedQuery, key);
  byKey.set(key, created);
  return created;
}
